import { useEffect, useRef, useState } from "react";
import { Play, Pause, RotateCcw, Coffee, Timer as TimerIcon } from "lucide-react";
import { toast } from "sonner";

const PRESETS = [
  { min: 15, label: "Quick 15" },
  { min: 25, label: "Classic 25" },
  { min: 45, label: "Deep 45" },
];

export default function FocusSprint() {
  const [duration, setDuration] = useState(25 * 60);
  const [remaining, setRemaining] = useState(25 * 60);
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState("focus"); // focus | break
  const audioRef = useRef(null);

  useEffect(() => { setRemaining(duration); }, [duration]);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(id);
          setRunning(false);
          if (audioRef.current) { try { audioRef.current.play(); } catch {} }
          if (phase === "focus") {
            setPhase("break"); setRemaining(90);
            toast.success("Nice focus! Take a 90-second stretch.");
          } else {
            setPhase("focus"); setRemaining(duration);
            toast("Break over — ready for another sprint?");
          }
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [running, phase, duration]);

  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");
  const pct = Math.round(((duration - remaining) / duration) * 100);

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="focus-page">
      <div>
        <div className="chip mb-2">Focus sprint</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Small window. Big win.</h1>
        <p className="text-slate-600 mt-1">Pick a length, start, and let the app handle the timing.</p>
      </div>

      <div className="p-10 rounded-3xl bg-white border border-slate-200 text-center">
        <div className="text-xs text-slate-500 uppercase tracking-widest flex items-center justify-center gap-1">
          {phase === "focus" ? <><TimerIcon size={12} /> Focus</> : <><Coffee size={12} /> Break</>}
        </div>
        <div data-testid="focus-clock" className="font-display text-7xl sm:text-8xl font-bold text-teal-700 tabular-nums mt-2">
          {mm}:{ss}
        </div>
        <div className="mt-4 h-2 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full bg-teal-600 transition-[width] duration-700" style={{ width: `${pct}%` }} />
        </div>
        <div className="mt-6 flex justify-center gap-2">
          {!running ? (
            <button data-testid="focus-start" onClick={()=>setRunning(true)}
              className="px-6 py-3 rounded-full bg-teal-700 hover:bg-teal-800 text-white font-semibold inline-flex items-center gap-2">
              <Play size={16} /> Start
            </button>
          ) : (
            <button data-testid="focus-pause" onClick={()=>setRunning(false)}
              className="px-6 py-3 rounded-full bg-amber-500 hover:bg-amber-600 text-white font-semibold inline-flex items-center gap-2">
              <Pause size={16} /> Pause
            </button>
          )}
          <button data-testid="focus-reset" onClick={()=>{ setRunning(false); setPhase("focus"); setRemaining(duration); }}
            className="px-6 py-3 rounded-full border border-slate-200 text-slate-700 font-semibold inline-flex items-center gap-2">
            <RotateCcw size={16} /> Reset
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {PRESETS.map((p) => (
          <button key={p.min} data-testid={`focus-preset-${p.min}`} onClick={() => { setDuration(p.min * 60); setPhase("focus"); setRunning(false); }}
            className={`px-4 py-2 rounded-full border text-sm font-semibold transition-colors ${duration === p.min * 60 ? "bg-teal-700 text-white border-teal-700" : "bg-white text-slate-700 border-slate-200 hover:border-teal-300"}`}>
            {p.label}
          </button>
        ))}
      </div>

      {/* Chime */}
      <audio ref={audioRef} preload="auto">
        <source src="data:audio/wav;base64,UklGRnQAAABXQVZFZm10IBAAAAABAAEAiBUAABCXAQABABgAZGF0YZAAAAB/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/" type="audio/wav" />
      </audio>
    </div>
  );
}
